import BaseTooltip from 'components/Tooltips/BaseTooltip';
import ResponderAlertModal from 'features/Responder/components/ResponderAlertModal';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import { deriveCheckpointsFromTour } from 'features/Responder/features/Tours/utils';
import React, { useMemo, useState } from 'react';
import { Button, Form, OverlayTrigger, Stack, Tooltip } from 'react-bootstrap';
import { flushSync } from 'react-dom';
import { FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import './tourList.css';

const renderResponderTooltip = (id, text) => (
  <Tooltip id={id} className="responder-tooltip">
    {text}
  </Tooltip>
);

/**
 * Simple tour picker list for responders.
 * Highlights the active tour and notifies TourContext on selection.
 * @returns {JSX.Element}
 */
export default function TourList() {
  const {
    tours,
    selectTour,
    activeTourData,
    deleteTour,
    fetchTourById,
    setBuilderTourData,
    builderHydrationRef,
    setTourEditing,
  } = useTours();
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isSelectionModeEnabled, setIsSelectionModeEnabled] = useState(false);
  const [selectedTourIds, setSelectedTourIds] = useState(() => new Set());
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);

  const selectedTours = useMemo(
    () => (Array.isArray(tours) ? tours.filter((tour) => selectedTourIds.has(`${tour.id}`)) : []),
    [selectedTourIds, tours],
  );
  const selectedTourCount = selectedTours.length;

  const resolveTourFromSource = async (tour) => {
    if (!tour) return null;
    const resolved = await fetchTourById?.(tour.id);
    return resolved || tour;
  };

  const handleSelectTour = async (tour) => {
    const resolvedTour = await resolveTourFromSource(tour);
    if (!resolvedTour) return;
    selectTour(resolvedTour);
  };

  const handleEditTour = async (tour) => {
    if (!tour) return;
    const resolvedTour = await resolveTourFromSource(tour);
    if (!resolvedTour) return;
    if (builderHydrationRef) {
      builderHydrationRef.current = resolvedTour.id ?? 'new';
    }
    flushSync(() => {
      setTourEditing(true);
    });
    setBuilderTourData(resolvedTour);
    selectTour(resolvedTour);
  };

  const handleCreateTour = () => {
    if (builderHydrationRef) {
      builderHydrationRef.current = null;
    }
    flushSync(() => {
      setTourEditing(true);
    });
    setBuilderTourData(null);
    selectTour(null);
  };

  const clearBulkSelection = () => {
    setSelectedTourIds(new Set());
    setIsBulkDeleteConfirmOpen(false);
  };

  const handleToggleSelectionMode = () => {
    setIsSelectionModeEnabled((current) => {
      if (current) clearBulkSelection();
      return !current;
    });
  };

  const handleTourSelectionChange = (tour, isSelected) => {
    if (tour?.id == null) return;
    const tourId = `${tour.id}`;
    setSelectedTourIds((current) => {
      const next = new Set(current);
      if (isSelected) {
        next.add(tourId);
      } else {
        next.delete(tourId);
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedTours.length) return;
    try {
      for (const tour of selectedTours) {
        await deleteTour(tour.id);
      }
    } catch (error) {
      console.error('Failed to delete selected tours:', error);
    }
    setIsBulkDeleteConfirmOpen(false);
    setIsSelectionModeEnabled(false);
    setSelectedTourIds(new Set());
  };

  return (
    <div className="w-100 d-flex flex-column gap-2 p-2 h-100" style={{ minHeight: 0 }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="mb-0">Manage Tours</h3>
        <div className="d-none d-lg-inline-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-interactive-action"
            onClick={handleToggleSelectionMode}
          >
            {isSelectionModeEnabled ? 'Done' : 'Edit'}
          </Button>
          {isSelectionModeEnabled && (
            <Button
              size="sm"
              variant="outline-danger"
              className="responder-interactive-action text-danger"
              disabled={selectedTourCount === 0}
              onClick={() => setIsBulkDeleteConfirmOpen(true)}
            >
              Delete Selected
            </Button>
          )}
          <OverlayTrigger
            placement="top"
            overlay={renderResponderTooltip('tour-list-create-top', 'Create tour')}
          >
            <Button
              variant="outline-primary"
              size="sm"
              className="responder-create-action responder-interactive-action d-flex align-items-center gap-2"
              onClick={handleCreateTour}
            >
              <FaPlus />
              <span>Create</span>
            </Button>
          </OverlayTrigger>
        </div>
      </div>
      <Stack gap={2} className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
        {Array.isArray(tours) && tours.length ? (
          tours.map((tour) => {
            const isActive = activeTourData && activeTourData.id === tour.id;
            const checkpointCount = deriveCheckpointsFromTour(tour)?.checkpoints?.length || 0;
            return (
              <div
                key={tour.id || tour.name}
                className={`tour-list-item responder-interactive-item ${
                  isActive ? 'is-selected' : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  handleSelectTour(tour);
                }}
                onKeyDown={async (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    await handleSelectTour(tour);
                  }
                }}
              >
                <div className="d-flex justify-content-between align-items-start py-2 px-3">
                  {isSelectionModeEnabled && (
                    <Form.Check
                      type="checkbox"
                      id={`tour-select-${tour.id}`}
                      className="me-2 mt-1"
                      checked={selectedTourIds.has(`${tour.id}`)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => handleTourSelectionChange(tour, event.target.checked)}
                      aria-label={`Select ${tour.name || 'Untitled Tour'}`}
                    />
                  )}
                  <div>
                    <div className="fw-semibold">{tour.name || 'Untitled Tour'}</div>
                    {checkpointCount ? (
                      <div className="small text-theme-muted">
                        {checkpointCount} checkpoint{checkpointCount === 1 ? '' : 's'}
                      </div>
                    ) : null}
                  </div>
                  <div className="d-flex flex-column align-items-center gap-2">
                    <BaseTooltip id={`tour-list-edit-${tour?.id}`} text="Edit tour">
                      <button
                        type="button"
                        className="tour-list-icon-btn responder-interactive-action text-primary"
                        aria-label={`Edit ${tour.name || 'tour'}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleEditTour(tour);
                        }}
                      >
                        <FaPencilAlt />
                      </button>
                    </BaseTooltip>
                    <BaseTooltip id={`tour-list-delete-${tour?.id}`} text="Delete tour">
                      <button
                        type="button"
                        className="tour-list-icon-btn responder-interactive-action text-danger"
                        aria-label={`Delete ${tour.name || 'tour'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete(tour);
                        }}
                      >
                        <FaTrash />
                      </button>
                    </BaseTooltip>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-theme-muted small">No tours saved yet.</div>
        )}
      </Stack>
      <div className="d-flex d-lg-none flex-column gap-2">
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            className="responder-interactive-action d-flex flex-grow-1 align-items-center justify-content-center"
            onClick={handleToggleSelectionMode}
          >
            {isSelectionModeEnabled ? 'Done' : 'Edit'}
          </Button>
          {isSelectionModeEnabled && (
            <Button
              variant="outline-danger"
              className="responder-interactive-action text-danger d-flex flex-grow-1 align-items-center justify-content-center"
              disabled={selectedTourCount === 0}
              onClick={() => setIsBulkDeleteConfirmOpen(true)}
            >
              Delete Selected
            </Button>
          )}
        </div>
        <OverlayTrigger
          placement="top"
          overlay={renderResponderTooltip('tour-list-create-bottom', 'Create tour')}
        >
          <Button
            variant="outline-primary"
            className="responder-create-action responder-interactive-action mt-2 d-flex w-100 align-items-center justify-content-center gap-2"
            onClick={handleCreateTour}
          >
            <FaPlus />
            <span>Create New Tour</span>
          </Button>
        </OverlayTrigger>
      </div>
      <ResponderAlertModal
        show={!!pendingDelete}
        title="Delete tour"
        message={`Are you sure you want to permanently delete "${
          pendingDelete?.name || 'this tour'
        }"? This action cannot be undone and will also delete all associated schedules.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onCancel={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (pendingDelete?.id == null) {
            setPendingDelete(null);
            return;
          }
          try {
            await deleteTour(pendingDelete.id);
          } catch (error) {
            console.error('Failed to delete tour:', error);
          }
          setPendingDelete(null);
        }}
      />
      <ResponderAlertModal
        show={isBulkDeleteConfirmOpen}
        title="Delete tours"
        message={`Are you sure you want to permanently delete ${selectedTourCount} selected tour${
          selectedTourCount === 1 ? '' : 's'
        }? This action cannot be undone and will also delete all associated schedules.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onCancel={() => setIsBulkDeleteConfirmOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  );
}
